/**
 * sRGB ↔ OKLCh. Không dùng thư viện ngoài: đây là ~80 dòng toán xác định,
 * và nó phải chạy được ở cả Node (server inject CSS) lẫn browser (admin live preview).
 *
 * OKLab: Björn Ottosson (2020). Lý do dùng OKLCh thay vì HSL: trong HSL, cùng một mức
 * "lightness" cho ra độ sáng cảm nhận rất khác nhau giữa vàng và xanh dương — nên palette
 * sinh từ HSL sẽ có bậc 500 của partner này chói hơn hẳn partner kia. OKLCh đều theo cảm nhận.
 */

export type Oklch = { l: number; c: number; h: number }
export type Rgb = { r: number; g: number; b: number }

const srgbToLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4)
const linearToSrgb = (c: number) => (c <= 0.0031308 ? c * 12.92 : 1.055 * c ** (1 / 2.4) - 0.055)

export function hexToRgb(hex: string): Rgb {
  const n = parseInt(hex.slice(1), 16)
  return { r: ((n >> 16) & 255) / 255, g: ((n >> 8) & 255) / 255, b: (n & 255) / 255 }
}

export function rgbToHex({ r, g, b }: Rgb): string {
  const to = (v: number) => Math.round(Math.min(1, Math.max(0, v)) * 255).toString(16).padStart(2, '0')
  return `#${to(r)}${to(g)}${to(b)}`
}

export function rgbToOklch({ r, g, b }: Rgb): Oklch {
  const lr = srgbToLinear(r), lg = srgbToLinear(g), lb = srgbToLinear(b)

  const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb)
  const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb)
  const s = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb)

  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s
  const A = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s
  const B = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s

  const c = Math.sqrt(A * A + B * B)
  const h = c < 1e-6 ? 0 : ((Math.atan2(B, A) * 180) / Math.PI + 360) % 360
  return { l: L, c, h }
}

function oklchToRgbRaw({ l: L, c, h }: Oklch): Rgb {
  const rad = (h * Math.PI) / 180
  const A = c * Math.cos(rad)
  const B = c * Math.sin(rad)

  const l = (L + 0.3963377774 * A + 0.2158037573 * B) ** 3
  const m = (L - 0.1055613458 * A - 0.0638541728 * B) ** 3
  const s = (L - 0.0894841775 * A - 1.291485548 * B) ** 3

  return {
    r: linearToSrgb(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s),
    g: linearToSrgb(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s),
    b: linearToSrgb(-0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s),
  }
}

const inGamut = ({ r, g, b }: Rgb) =>
  r >= -1e-4 && r <= 1 + 1e-4 && g >= -1e-4 && g <= 1 + 1e-4 && b >= -1e-4 && b <= 1 + 1e-4

/**
 * Ép về gamut sRGB bằng cách GIẢM CHROMA, không phải clamp từng kênh.
 * Clamp kênh làm lệch hue — đỏ tươi hoá cam. Giảm chroma giữ nguyên hue và lightness,
 * chỉ nhạt màu đi đúng mức tối thiểu để hiển thị được.
 */
export function oklchToRgb(color: Oklch): Rgb {
  const direct = oklchToRgbRaw(color)
  if (inGamut(direct)) return direct

  let lo = 0
  let hi = color.c
  for (let i = 0; i < 16; i++) {
    const mid = (lo + hi) / 2
    if (inGamut(oklchToRgbRaw({ ...color, c: mid }))) lo = mid
    else hi = mid
  }
  return oklchToRgbRaw({ ...color, c: lo })
}

export const hexToOklch = (hex: string): Oklch => rgbToOklch(hexToRgb(hex))
export const oklchToHex = (color: Oklch): string => rgbToHex(oklchToRgb(color))
